<template>
	<div class="card relative z-10 overflow-hidden shadow-lg border border-gray-200/50 dark:border-gray-700/50">
		<!-- Desktop Table View -->
		<div class="hidden lg:block">
			<table class="w-full divide-y divide-gray-200/50 dark:divide-gray-700/50 text-slate-700 dark:text-slate-300 table-fixed">
				<thead class="bg-gradient-to-r from-primary/5 via-primary/3 to-transparent dark:from-primary-dark/10 dark:via-primary-dark/5 dark:to-transparent">
					<tr>
						<th
						scope="col"
						class="px-4 xl:px-6 py-4 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider w-32"
						>
						Name
						</th>
						<th
						scope="col"
						class="hidden xl:table-cell px-4 xl:px-6 py-4 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider w-32"
						>
						Phone
						</th>
						<th
						scope="col"
						class="px-4 xl:px-6 py-4 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider w-32"
						>
						Status
						</th>
						<th
						scope="col"
						class="hidden xl:table-cell px-4 xl:px-6 py-4 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider w-36"
						>
						Last Updated
						</th>
						<th
						scope="col"
						class="px-4 xl:px-6 py-4 text-center text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider w-40"
						>
						Actions
						</th>
					</tr>
				</thead>
				<tbody class="bg-white dark:bg-gray-900/50 divide-y divide-gray-200/50 dark:divide-gray-700/50">
					<TableLoader v-if="loading" :colspan="5" title="Fetching Employees"/>
					<template v-else>
						<EmployeeRow
							v-for="employee in safeEmployees"
							:key="employee.id"
							:employee="employee"
							@view="$emit('view', $event)"
							@activate="$emit('activate', $event)"
							@deactivate="$emit('deactivate', $event)"
							@approve="$emit('approve', $event)"
							@delete="$emit('delete', $event)"
						/>
					</template>
				</tbody>
			</table>
		</div>

		<!-- Mobile Card View -->
		<div v-if="loading" class="lg:hidden flex justify-center py-16">
			<Loader2 class="h-8 w-8 animate-spin text-primary" />
		</div>
		<div v-else class="lg:hidden divide-y divide-gray-200/50 dark:divide-gray-700/50">
			<EmployeeCard
				v-for="employee in safeEmployees"
				:key="employee.id"
				:employee="employee"
				@view="$emit('view', $event)"
				@activate="$emit('activate', $event)"
				@deactivate="$emit('deactivate', $event)"
				@approve="$emit('approve', $event)"
				@delete="$emit('delete', $event)"
			/>
		</div>

		<!-- Empty State -->
		<div
			v-if="!loading && safeEmployees.length === 0"
			class="text-center py-16 px-4"
		>
			<div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 mb-4">
				<Users class="w-8 h-8 text-secondary-text/50 dark:text-secondary-dark-text/50" />
			</div>
			<p class="text-base font-medium text-secondary-text dark:text-secondary-dark-text">
				No employees found
			</p>
			<p class="text-sm text-secondary-text/70 dark:text-secondary-dark-text/70 mt-1">
				Try adjusting your search or filters
			</p>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Users, Loader2 } from 'lucide-vue-next'
import type { Employee } from '@/services/employeeService'
import EmployeeRow from './EmployeeRow.vue'
import EmployeeCard from './EmployeeCard.vue'
import TableLoader from '@/components/ui/TableLoader.vue'

const props = defineProps<{
  employees: Employee[]
  loading?: boolean
}>()

const safeEmployees = computed(() =>
  (props.employees ?? []).filter((e): e is Employee => !!e && typeof (e as any).id === 'number')
)

// const emit = defineEmits<{
// 	(e: 'view', id: number): void
// 	(e: 'activate', id: number): void
// 	(e: 'deactivate', id: number): void
// 	(e: 'approve', id: number): void
// 	(e: 'delete', id: number): void
// }>()
</script>
